import {useState, useRef, forwardRef, useEffect, useCallback} from 'react';
import classNames from 'classnames';
import {useIntl} from 'react-intl';

import Input, {InputRef} from '@/components/Input';
import Icon from '@/components/Icon';
import toast, {ToastType} from '@/components/Toast';
import {TagSvg, EnterSvg, AddTagSvg} from '@/icons';
import {feLogger, addTagType, TagType} from '@/buried';

import {trim, strLen, cutStr} from '@/utils/strUtils';
import {TagData} from '../interface';

import styles from './index.module.scss';

const inputPaddingAndBorder = 50;

interface IProps {
    tags: TagData[];
    maxTags: number;
    maxTagName: number;
    onAdd: (tagName: TagData['name']) => Promise<string | null | void>;
}

const TagAdd = forwardRef<HTMLDivElement, IProps>(({tags, maxTags, maxTagName, onAdd}, ref) => {
    const intl = useIntl();
    const [value, setValue] = useState('');
    const [isFocus, setIsFocus] = useState(false);
    const [exceedMaxLength, setExceedMaxLength] = useState(false);
    const isCompositionEnd = useRef(true);
    const inputRef = useRef<InputRef>(null);

    const disabled = tags.length >= maxTags;

    function changeValue(str: string) {
        if (!isCompositionEnd.current) {
            setValue(str);
            return;
        }
        if (strLen(str) > maxTagName) {
            setValue(cutStr(str, maxTagName));
            setExceedMaxLength(true);
            return;
        }
        setValue(str);
        setExceedMaxLength(false);
    }
    function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
        const inputValue = e.target.value || '';
        changeValue(inputValue);
    }

    const [inputWidth, setInputWidth] = useState(0);
    const shadowInputRef = useRef<HTMLDivElement | null>(null);
    const updateWidth = useCallback(() => {
        if (shadowInputRef.current) {
            setInputWidth(shadowInputRef.current.offsetWidth + inputPaddingAndBorder);
        }
    }, []);

    useEffect(() => {
        updateWidth();
    }, [updateWidth, value]);

    useEffect(() => {
        return () => {
            setValue('');
            setInputWidth(0);
        };
    }, []);

    function handleFocus() {
        setIsFocus(true);
    }
    function handleBlur() {
        setIsFocus(false);
    }

    async function handleEnter() {
        if (tags.length >= maxTags) {
            toast({
                content: intl.formatMessage({id: 'textMaxTagTips'}, {maxLength: maxTags}),
                type: ToastType.Error,
            });
            return;
        }
        const trimValue = trim(value);
        if (!trimValue) {
            return;
        }
        if (strLen(trimValue) > maxTagName) {
            return;
        }
        const sameNameIndex = tags.findIndex(tag => {
            return tag.name === trimValue;
        });
        if (sameNameIndex >= 0) {
            toast({
                content: intl.formatMessage({id: 'textDuplicateTagTips'}),
                type: ToastType.Error,
            });
            return;
        }
        try {
            feLogger(addTagType({tagCont: trimValue, type: TagType.selfSelect}));
            onAdd && (await onAdd(trimValue));
            changeValue('');
            inputRef.current && inputRef.current.blur();
        } catch (err) {
            //
        }
    }

    function handlingComposition() {
        isCompositionEnd.current = false;
    }
    function handleComposition(e: React.CompositionEvent<HTMLInputElement>) {
        const inputValue = (e.target as any).value || '';
        isCompositionEnd.current = true;
        changeValue(inputValue);
    }

    const placeholder = disabled ? `已满${maxTags}个` : isFocus ? '' : '添加标签';

    return (
        <div
            className={classNames(styles.inputWrapper, {[styles.focus]: isFocus, [styles.warning]: exceedMaxLength})}
            ref={ref}
        >
            <div ref={shadowInputRef} className={styles.shadowInput}>
                {value}
            </div>
            <Icon className={styles.tagIcon} svg={TagSvg} />
            <Input
                ref={inputRef}
                disabled={disabled}
                className={classNames(styles.input, {[styles.warning]: exceedMaxLength, [styles.disabled]: disabled})}
                placeholder={placeholder}
                onCompositionStart={handlingComposition}
                onCompositionUpdate={handlingComposition}
                onCompositionEnd={handleComposition}
                value={value}
                onChange={handleChange}
                onPressEnter={handleEnter}
                onFocus={handleFocus}
                onBlur={handleBlur}
                style={{width: `${inputWidth}px`}}
            />
            {!disabled && (
                <>
                    {value ? (
                        <Icon
                            svg={EnterSvg}
                            className={classNames(styles.suffixIcon, styles.enterIcon, {[styles.active]: value})}
                            onClick={handleEnter}
                        />
                    ) : (
                        <Icon svg={AddTagSvg} className={styles.suffixIcon} />
                    )}
                </>
            )}
        </div>
    );
});

export default TagAdd;
